import type { CSSProperties } from "react"

/**
 * The jacket feed's type ramp (designer handoff — "Vinyl Covers"), scoped as
 * inline styles so the global `--font-body` / `--font-display` tokens stay
 * untouched — the same scoping the /design "Type candidates" specimen uses.
 * Four voices: Zilla Slab (DISPLAY — titles + section heads, a slab that
 * carries the short display head at weight), Source Serif 4 (SERIF — body,
 * liner notes, signatures, reader), Space Mono (MONO — kickers, tags,
 * catalog marks), Hanken Grotesk (SANS — the stage byline / UI text that the
 * comps never re-voiced). Zilla Slab, Source Serif 4 and Space Mono load from
 * Google Fonts via <JacketFonts/> (see jacket-fonts.tsx for the self-hosting
 * teardown); Hanken is self-hosted.
 */

export const DISPLAY = '"Zilla Slab", Georgia, serif'
export const SERIF = '"Source Serif 4", Georgia, serif'
export const SANS = '"Hanken Grotesk", -apple-system, "Segoe UI", sans-serif'
export const MONO = '"Space Mono", "Courier New", Courier, monospace'

/**
 * Jacket title — Besley at display weight; carries the SHORT display head
 * (display-title.ts strips the platform tail), which is why it can run
 * this heavy. The `cqi` clamp needs an `@container` ancestor (the jacket
 * face root) or it silently resolves against the viewport. The SleeveCard
 * overrides the ramp with per-breakpoint `--title-fs` (v4 §8: type and
 * composition are ONE tuning problem once the card scales with the
 * canvas); surfaces outside the card — the gatefold reprint, PaperOutro —
 * fall through to the v3 clamp.
 */
export const jacketTitle: CSSProperties = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: "var(--title-fs, clamp(34px, 9cqi, 56px))",
  lineHeight: 0.96,
  letterSpacing: "-0.01em",
  textAlign: "left",
  color: "var(--color-batyr-ink)",
}

/** Standfirst / liner notes on jacket faces: 15px/1.72, full Ink — the
 * mockup's body voice, never scaled up to fill space (the 52/15 pair is
 * the ~3.5:1 hierarchy). The sleeve face opts OUT of the 62ch cap
 * (maxWidth none — the square's book frame ends the line; a bound cap
 * re-created the document rag); 62ch stays here as the net for no-gutter
 * consumers (gatefold reprint, PaperOutro). The SleeveCard sets
 * `--notes-fs` per breakpoint (15.5 phone / 15 desktop); elsewhere the
 * 15px fallback holds. */
export const linerNotes: CSSProperties = {
  fontFamily: SERIF,
  fontWeight: 400,
  fontSize: "var(--notes-fs, 15px)",
  lineHeight: 1.72,
  maxWidth: "62ch",
  color: "var(--color-batyr-ink)",
}

/**
 * Chrome labels and catalog marks — the typewriter voice: marks, not
 * prose. Author label strings LOWERCASE — `all-small-caps` only transforms
 * lowercase glyphs, so literal capitals would render as the banned
 * bold-uppercase. (Courier Prime ships no smcp feature either; the
 * synthesized small caps are the accepted rendering. Weight stays 400 —
 * Courier Prime has no 600 cut, and a bold mark drifts toward stamp-shout.)
 */
export const chromeLabel: CSSProperties = {
  fontFamily: MONO,
  fontWeight: 400,
  fontSize: 11,
  letterSpacing: "0.08em",
  fontVariantCaps: "all-small-caps",
}

/**
 * The kicker voice — the mockup's mark: Courier Prime 13px with 3px
 * tracking in slate, on a pinned 16px line box. Speaks on the card
 * (CatalogMark on the sleeve face and gatefold reprint) and as the
 * PlaylistMark on the top chrome row — the retired vertical spine's
 * words, relocated. chromeLabel (11px, above) stays the small-mark voice
 * — dock spines, stage placards, mute hint, end cap — which the mockup
 * never spoke to.
 */
export const kicker: CSSProperties = {
  fontFamily: MONO,
  fontWeight: 400,
  fontSize: 13,
  lineHeight: "16px",
  letterSpacing: 3,
  fontVariantCaps: "all-small-caps",
  color: "var(--jacket-slate, #8a94a6)",
}

/** Byline block — curator name leads at 13.5/600 in full Ink (the mockup
 * retired Byline Blue for attribution; the 600 static is self-hosted)… */
export const bylineName: CSSProperties = {
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 13.5,
  color: "var(--color-batyr-ink)",
}

/** …media creator recedes in slate after the em-dash — same 13.5 (a mixed
 * size on one line reads as baseline wobble), receding by color alone. */
export const bylineCreator: CSSProperties = {
  fontFamily: SANS,
  fontWeight: 500,
  fontSize: 13.5,
  color: "var(--jacket-slate, rgb(var(--batyr-ink-rgb) / 0.55))",
}

/** Marginalia — the curator's pencil, used sparingly. Ink/60 by default. */
export const marginalia: CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontWeight: 400,
  color: "rgb(var(--batyr-ink-rgb) / 0.6)",
}

/** Marginalia in the red-pencil voice — marks beside words, never body text. */
export const marginaliaRedPencil: CSSProperties = {
  ...marginalia,
  color: "var(--color-batyr-red-pencil)",
}

/** Article reader row (§3.3): 18px/1.6, 65ch, oldstyle figures. */
export const readerBody: CSSProperties = {
  fontFamily: SERIF,
  fontWeight: 400,
  fontSize: 18,
  lineHeight: 1.6,
  maxWidth: "65ch",
  fontVariantNumeric: "oldstyle-nums",
  color: "rgb(var(--batyr-ink-rgb) / 0.92)",
}

/* -------------------------------------------------- liner-card credit voices */

/**
 * The liner card's format tag (the record-sleeve kicker): Space Mono, wide
 * tracked uppercase in slate — e.g. "B1 · PRINT" (side + medium). Full-size
 * caps, not the small-mark chromeLabel — this is the card's masthead line.
 */
export const formatTag: CSSProperties = {
  fontFamily: MONO,
  fontWeight: 400,
  fontSize: 14,
  letterSpacing: "0.28em",
  textTransform: "uppercase",
  color: "var(--jacket-slate, #8a94a6)",
}

/**
 * The curator's name as a hand — Source Serif 4 italic, in masthead navy.
 * This is the personal attribution ("who wrote the note"), signed rather
 * than badged. Sized to sit as a suffix beside the avatar, not as a
 * standalone masthead.
 */
export const signature: CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: 16,
  lineHeight: 1,
  color: "var(--color-batyr-ink)",
}
